import { z } from 'zod';
import { STARTING_SITUATIONS, type StartingSituation } from './economy';
import { containsBlockedWord } from './profile';

// A character is a life story plus a look (docs AVATARS.md). The look is a set of option keys from a
// catalogue admins manage, never a 3D file, so new hair or outfits never break a saved character.

// ---- life story

// how a UK life starts (Jerry, 9 October 2026). Every origin except the first comes with a country, any country
// in the world; UK-born players may add a family heritage. No origin changes what a player can choose or do.
export const ORIGINS = {
  uk_born: 'Born and raised in the UK',
  moved_as_child: 'Moved here as a child',
  came_for_work: 'Came for work',
  international_student: 'International student',
  joined_family: 'Joined family',
  working_holiday: 'Working holiday',
  seeking_safety: 'Seeking safety',
} as const;
export const ORIGIN_HINTS: Record<keyof typeof ORIGINS, string> = {
  uk_born: 'Grew up somewhere in the UK',
  moved_as_child: 'Born abroad, grew up here',
  came_for_work: 'A job offer or a fresh start',
  international_student: 'Here to study',
  joined_family: 'Came to be with a partner or family',
  working_holiday: 'Young and here for a year or two',
  seeking_safety: 'Arrived as a refugee or asylum seeker',
};
export type Origin = keyof typeof ORIGINS;

export const UK_HOME_REGIONS = {
  london: 'London',
  south_east: 'South East England',
  south_west: 'South West England',
  east_of_england: 'East of England',
  east_midlands: 'East Midlands',
  west_midlands: 'West Midlands',
  yorkshire: 'Yorkshire and the Humber',
  north_west: 'North West England',
  north_east: 'North East England',
  wales: 'Wales',
  scotland: 'Scotland',
  northern_ireland: 'Northern Ireland',
} as const;
export type UkHomeRegion = keyof typeof UK_HOME_REGIONS;

// every ISO 3166-1 country; names come from the browser's own list (Intl.DisplayNames) in the player's language
export const COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO ' +
  'FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE ' +
  'JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO ' +
  'MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW ' +
  'PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM ' +
  'TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');
const COUNTRY_SET = new Set(COUNTRY_CODES);

const Situation = z.enum(Object.keys(STARTING_SITUATIONS) as [StartingSituation, ...StartingSituation[]]);
const Country = z.string().refine((code) => COUNTRY_SET.has(code) && code !== 'GB', 'Choose a country outside the UK');
const abroad = (origin: Exclude<Origin, 'uk_born'>) =>
  z.object({ origin: z.literal(origin), country: Country, situation: Situation }).strict();
export const LifeStory = z.discriminatedUnion('origin', [
  z
    .object({
      origin: z.literal('uk_born'),
      homeRegion: z.enum(Object.keys(UK_HOME_REGIONS) as [UkHomeRegion, ...UkHomeRegion[]]),
      // optional: the country (or countries' first) a UK-born player's family comes from
      heritage: Country.nullable().optional(),
      situation: Situation,
    })
    .strict(),
  abroad('moved_as_child'),
  abroad('came_for_work'),
  abroad('international_student'),
  abroad('joined_family'),
  abroad('working_holiday'),
  abroad('seeking_safety'),
]);
export type LifeStory = z.infer<typeof LifeStory>;

// ---- look

export const CHARACTER_OPTION_CATEGORIES = {
  hair: 'Hair',
  hair_colour: 'Hair colour',
  facial_hair: 'Facial hair',
  outfit: 'Outfit',
  accessory: 'Accessories',
} as const;
export type CharacterOptionCategory = keyof typeof CHARACTER_OPTION_CATEGORIES;
// body shape for the 3D character, separate from gender (below); in-between blends the two shapes
export const BODY_FRAMES = { feminine: 'Feminine', androgynous: 'In-between', masculine: 'Masculine' } as const;
export type BodyFrame = keyof typeof BODY_FRAMES;
const Frame = z.enum(['feminine', 'androgynous', 'masculine']);

// who the character is: gender and pronouns, chosen from fixed lists (no free typing) and never tied to the
// body or the look. UK law recognises male and female; the list follows how people in the UK describe
// themselves (Jerry, 9 October 2026).
export const GENDERS = {
  woman: 'Woman',
  man: 'Man',
  non_binary: 'Non-binary',
  trans_woman: 'Trans woman',
  trans_man: 'Trans man',
  genderfluid: 'Genderfluid',
  agender: 'Agender',
  intersex: 'Intersex',
  prefer_not_to_say: 'Prefer not to say',
} as const;
export type Gender = keyof typeof GENDERS;
export const PRONOUNS = { she_her: 'she/her', he_him: 'he/him', they_them: 'they/them', any: 'any pronouns' } as const;
export const Identity = z
  .object({
    gender: z.enum(Object.keys(GENDERS) as [Gender, ...Gender[]]),
    pronouns: z.enum(Object.keys(PRONOUNS) as [keyof typeof PRONOUNS, ...(keyof typeof PRONOUNS)[]]),
  })
  .strict();
export type Identity = z.infer<typeof Identity>;
export const DEFAULT_IDENTITY: Identity = { gender: 'prefer_not_to_say', pronouns: 'they_them' };
export const BUILDS = { slim: 'Slim', average: 'Average', broad: 'Broad' } as const;
export const SKIN_TONES = 12;
export const MAX_ACCESSORIES = 3;

const optionKey = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/);
export const Appearance = z
  .object({
    frame: Frame,
    build: z.enum(['slim', 'average', 'broad']),
    skinTone: z.number().int().min(1).max(SKIN_TONES),
    hair: optionKey,
    hairColour: optionKey,
    facialHair: optionKey.nullable(),
    outfit: optionKey,
    accessories: z.array(optionKey).max(MAX_ACCESSORIES),
  })
  .strict();
export type Appearance = z.infer<typeof Appearance>;

export const CharacterOption = z.object({
  category: z.enum(Object.keys(CHARACTER_OPTION_CATEGORIES) as [CharacterOptionCategory, ...CharacterOptionCategory[]]),
  key: optionKey,
  label: z.string().min(2).max(60),
  // which body frames it fits; empty means every frame
  frames: z.array(Frame),
  sortOrder: z.number().int(),
});
export type CharacterOption = z.infer<typeof CharacterOption>;
export const CharacterOptionsReply = z.object({
  options: z.array(CharacterOption),
  // what each situation starts with, so the creator can show it before the player chooses
  startingMoneyPence: z.record(z.string(), z.number().int()),
});
export type CharacterOptionsReply = z.infer<typeof CharacterOptionsReply>;

// the starting catalogue (docs AVATARS.md, core set); admins add, hide and reorder from the dashboard later.
// Nothing is tied to an origin, ethnicity, body or gender: every option is open to every player.
type Seed = [CharacterOptionCategory, string, string, BodyFrame[]?];
const SEED: Seed[] = [
  ['hair', 'short_crop', 'Short crop'],
  ['hair', 'buzz_cut', 'Buzz cut'],
  ['hair', 'waves', 'Waves'],
  ['hair', 'high_top', 'High top'],
  ['hair', 'afro', 'Afro'],
  ['hair', 'twist_out', 'Twist-out'],
  ['hair', 'box_braids', 'Box braids'],
  ['hair', 'cornrows', 'Cornrows'],
  ['hair', 'locs', 'Locs'],
  ['hair', 'long_straight', 'Long straight'],
  ['hair', 'wavy_bob', 'Wavy bob'],
  ['hair', 'curly_bob', 'Curly bob'],
  ['hair', 'bun', 'Bun'],
  ['hair', 'ponytail', 'Ponytail'],
  ['hair', 'hijab', 'Hijab'],
  ['hair', 'headwrap', 'Headwrap'],
  ['hair_colour', 'black', 'Black'],
  ['hair_colour', 'dark_brown', 'Dark brown'],
  ['hair_colour', 'brown', 'Brown'],
  ['hair_colour', 'auburn', 'Auburn'],
  ['hair_colour', 'blonde', 'Blonde'],
  ['hair_colour', 'grey', 'Grey'],
  ['facial_hair', 'stubble', 'Stubble'],
  ['facial_hair', 'short_beard', 'Short beard'],
  ['facial_hair', 'full_beard', 'Full beard'],
  ['facial_hair', 'moustache', 'Moustache'],
  ['outfit', 'casual', 'Casual'],
  ['outfit', 'smart_casual', 'Smart casual'],
  ['outfit', 'office', 'Office'],
  ['outfit', 'hi_vis', 'Hi-vis trades'],
  ['outfit', 'scrubs', 'Hospital scrubs'],
  ['outfit', 'tracksuit_mask', 'Tracksuit and mask'],
  ['outfit', 'student_hoodie', 'Student hoodie'],
  ['outfit', 'agbada', 'Agbada'],
  ['outfit', 'kaftan', 'Kaftan'],
  ['outfit', 'ankara_dress', 'Ankara dress'],
  ['outfit', 'kente', 'Kente'],
  ['outfit', 'sari', 'Sari'],
  ['outfit', 'salwar_kameez', 'Salwar kameez'],
  ['outfit', 'abaya', 'Abaya'],
  ['outfit', 'thobe', 'Thobe'],
  ['outfit', 'sunday_best', 'Sunday best'],
  ['accessory', 'glasses', 'Glasses'],
  ['accessory', 'sunglasses', 'Sunglasses'],
  ['accessory', 'cap', 'Cap'],
  ['accessory', 'beanie', 'Beanie'],
  ['accessory', 'gele', 'Gele'],
  ['accessory', 'turban', 'Turban'],
  ['accessory', 'earrings', 'Earrings'],
];
export const DEFAULT_CHARACTER_OPTIONS: CharacterOption[] = SEED.map(([category, key, label, frames], index) => ({
  category,
  key,
  label,
  frames: frames ?? [],
  sortOrder: index,
}));

// ---- lives

export const FirstName = z
  .string()
  .trim()
  .min(2, 'Use at least 2 letters')
  .max(20, 'Use 20 letters or fewer')
  .regex(/^\p{L}[\p{L}' -]*$/u, 'Use letters, spaces, hyphens or apostrophes')
  .refine((name) => !containsBlockedWord(name), 'Please choose a different name');
export const LifeCreate = z
  .object({ firstName: FirstName, identity: Identity, story: LifeStory, appearance: Appearance })
  .strict();
export type LifeCreate = z.infer<typeof LifeCreate>;

export const LifeView = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  identity: Identity,
  story: LifeStory,
  appearance: Appearance,
  walletPence: z.number().int(),
  startingMoneyPence: z.number().int(),
  createdAt: z.string(),
  // personality traits (play.ts TRAITS) and when they may next be changed (null: now)
  traits: z.array(z.string()),
  traitsChangeFrom: z.string().nullable(),
});
export type LifeView = z.infer<typeof LifeView>;
export const MyLifeReply = z.object({
  life: LifeView.nullable(),
  // when the player may start another life (the 7-day wait), or null if they may now
  newLifeFrom: z.string().nullable(),
});
export type MyLifeReply = z.infer<typeof MyLifeReply>;
export const LifeEnd = z.object({ confirm: z.literal(true) }).strict();
export const NEW_LIFE_WAIT_DAYS = 7;
